import React, { useState } from 'react';
import { Phone, MessageCircle, MapPin, Send, CheckCircle, Loader2, Clock, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { contactInfo } from '../data/mock';
import { toast } from 'sonner';

// Constants
const SUBMIT_DELAY_MS = 1500;
const RESET_DELAY_MS = 3000;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    await new Promise(resolve => setTimeout(resolve, SUBMIT_DELAY_MS));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
    toast.success('Message envoyé avec succès ! Le marabout vous contactera très bientôt.');
    
    setTimeout(() => {
      setFormData({ name: '', phone: '', subject: '', message: '' });
      setIsSubmitted(false);
    }, RESET_DELAY_MS);
  };

  return (
    <section id="contact" className="py-16 sm:py-28 bg-gradient-to-b from-black via-gray-950 to-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header - H2 SEO */}
        <div className="text-center mb-12 sm:mb-20">
          <span className="inline-block px-4 sm:px-5 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-500 text-xs sm:text-sm font-semibold tracking-wide mb-4 sm:mb-6">
            Contactez le Grand Marabout
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-8">
            Consultation <span className="text-amber-500">Retour Affectif</span>
          </h2>
          <p className="text-gray-300 text-base sm:text-xl max-w-3xl mx-auto leading-relaxed px-2">
            Le grand marabout africain est à votre écoute pour analyser votre situation et vous proposer la meilleure solution pour récupérer votre ex.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-16">
          {/* Contact Methods */}
          <div className="space-y-4 sm:space-y-8">
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-6 sm:mb-10">Appelez le Marabout</h3>
            
            {/* Phone */}
            <a
              href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
              className="flex items-center space-x-4 sm:space-x-6 p-5 sm:p-8 bg-gray-900/60 border border-amber-500/20 rounded-xl sm:rounded-2xl hover:border-amber-500/50 transition-all duration-300 group"
            >
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0 bg-amber-500/10 group-hover:bg-amber-500/20 transition-colors">
                <Phone className="w-5 h-5 sm:w-7 sm:h-7 text-amber-500" />
              </div>
              <div className="flex-grow min-w-0">
                <h4 className="text-white font-bold text-lg sm:text-xl mb-1 group-hover:text-amber-500 transition-colors">
                  Téléphone Direct
                </h4>
                <p className="text-amber-500 font-bold text-lg sm:text-2xl truncate">{contactInfo.phone}</p>
                <p className="text-gray-400 text-sm sm:text-base mt-1">Disponible 7j/7 - 24h/24</p>
              </div>
            </a>

            {/* WhatsApp */}
            <a
              href={contactInfo.whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-4 sm:space-x-6 p-5 sm:p-8 bg-gray-900/60 border border-green-500/20 rounded-xl sm:rounded-2xl hover:border-green-500/50 transition-all duration-300 group"
            >
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0 bg-green-600/20 group-hover:bg-green-600/30 transition-colors">
                <MessageCircle className="w-5 h-5 sm:w-7 sm:h-7 text-green-500" />
              </div>
              <div className="flex-grow min-w-0">
                <h4 className="text-white font-bold text-lg sm:text-xl mb-1 group-hover:text-green-500 transition-colors">
                  WhatsApp Direct
                </h4>
                <p className="text-green-500 font-bold text-lg sm:text-2xl truncate">{contactInfo.phone}</p>
                <p className="text-gray-400 text-sm sm:text-base mt-1">Réponse rapide garantie</p>
              </div>
            </a>

            {/* Location Info */}
            <div className="p-5 sm:p-8 bg-gray-900/60 border border-amber-500/20 rounded-xl sm:rounded-2xl">
              <div className="flex items-center space-x-4 sm:space-x-6">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-amber-500/10 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 sm:w-7 sm:h-7 text-amber-500" />
                </div>
                <div>
                  <h4 className="text-white font-bold text-lg sm:text-xl mb-1">Zone d'intervention mondiale</h4>
                  <p className="text-amber-500 font-bold text-lg sm:text-xl">France, Belgique, Suisse, Canada, Afrique</p>
                  <p className="text-gray-400 text-sm sm:text-base mt-1">Rituels efficaces à distance</p>
                </div>
              </div>
            </div>

            {/* Trust badges */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4 mt-6 sm:mt-8">
              <div className="flex items-center space-x-2 sm:space-x-3 p-3 sm:p-4 bg-gray-900/40 rounded-lg sm:rounded-xl">
                <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500 flex-shrink-0" />
                <span className="text-gray-300 text-xs sm:text-base font-medium">100% Confidentiel</span>
              </div>
              <div className="flex items-center space-x-2 sm:space-x-3 p-3 sm:p-4 bg-gray-900/40 rounded-lg sm:rounded-xl">
                <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500 flex-shrink-0" />
                <span className="text-gray-300 text-xs sm:text-base font-medium">Réponse immédiate</span>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-gray-900/60 border border-amber-500/20 rounded-2xl sm:rounded-3xl p-6 sm:p-10">
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-6 sm:mb-8">Décrivez votre situation</h3>
            
            {isSubmitted ? (
              <div className="flex flex-col items-center justify-center py-12 sm:py-16 text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-500/20 rounded-full flex items-center justify-center mb-4 sm:mb-6">
                  <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 text-green-500" />
                </div>
                <h4 className="text-xl sm:text-2xl font-bold text-white mb-2 sm:mb-3">Message envoyé !</h4>
                <p className="text-gray-300 text-base sm:text-lg">Le grand marabout vous contactera très bientôt.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div className="space-y-2 sm:space-y-3">
                    <Label htmlFor="name" className="text-gray-200 text-sm sm:text-base font-medium">Nom complet *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Votre nom"
                      className="bg-gray-800/60 border-amber-500/20 text-white text-base sm:text-lg placeholder:text-gray-500 focus:border-amber-500 h-12 sm:h-14 rounded-lg sm:rounded-xl"
                    />
                  </div>
                  <div className="space-y-2 sm:space-y-3">
                    <Label htmlFor="phone" className="text-gray-200 text-sm sm:text-base font-medium">Téléphone *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="Votre numéro"
                      className="bg-gray-800/60 border-amber-500/20 text-white text-base sm:text-lg placeholder:text-gray-500 focus:border-amber-500 h-12 sm:h-14 rounded-lg sm:rounded-xl"
                    />
                  </div>
                </div>

                <div className="space-y-2 sm:space-y-3">
                  <Label htmlFor="subject" className="text-gray-200 text-sm sm:text-base font-medium">Votre besoin *</Label>
                  <Input
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    placeholder="Ex: Retour affectif, Récupérer mon ex, Faire revenir l'être aimé..."
                    className="bg-gray-800/60 border-amber-500/20 text-white text-base sm:text-lg placeholder:text-gray-500 focus:border-amber-500 h-12 sm:h-14 rounded-lg sm:rounded-xl"
                  />
                </div>

                <div className="space-y-2 sm:space-y-3">
                  <Label htmlFor="message" className="text-gray-200 text-sm sm:text-base font-medium">Votre situation *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder="Décrivez votre situation en détail pour que le marabout puisse vous aider..."
                    className="bg-gray-800/60 border-amber-500/20 text-white text-base sm:text-lg placeholder:text-gray-500 focus:border-amber-500 resize-none rounded-lg sm:rounded-xl"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold text-base sm:text-lg py-5 sm:py-7 rounded-lg sm:rounded-xl transition-all duration-300 hover:scale-[1.02]"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3" />
                      Envoyer au marabout
                    </>
                  )}
                </Button>

                <p className="text-gray-400 text-xs sm:text-sm text-center">
                  Vos informations restent strictement confidentielles. Le grand marabout vous répond personnellement.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
